import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "components/loader/Loader";
import { ErrorState } from "components/states";
import { calculateAssessmentResults } from "utils/assessmentScoring";
import PublicSurveyShell from "views/public/shared/PublicSurveyShell";
import SurveyProgress from "views/public/shared/SurveyProgress";
import { PUBLIC_AUDIENCES } from "views/public/shared/audiences";
import { isRetryableError } from "views/public/shared/errors";
import { applyAnswer, buildResponses, organizationOf, resolveQuestions } from "./questions";
import AssessmentIntro from "./AssessmentIntro";
import AssessmentQuestion from "./AssessmentQuestion";
import AssessmentResults from "./AssessmentResults";

/**
 * Token-based intake assessment opened from an emailed/SMS link:
 * /staff-intake/t/:token and /volunteer-intake/t/:token.
 * The person is already known, so the profile and verify steps are skipped.
 *
 * Phases: intro → questions → submitting → results (or submit-error).
 */
const IntakeTokenPage = ({ audience }) => {
  const { service } = PUBLIC_AUDIENCES[audience];
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [submissionToken, setSubmissionToken] = useState(null);
  const [userData, setUserData] = useState(null);

  const [phase, setPhase] = useState("intro");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({ partA: {}, partB: {} });
  const [results, setResults] = useState(null);

  const questions = useMemo(() => resolveQuestions(form), [form]);
  const totalQuestions = questions.length;
  const organization = organizationOf(form);

  const fetchFormByToken = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const response = await service.getIntakeFormByToken(token);
      if (response.success) {
        const { form: tokenForm, submissionToken: tokenSubmission, userData: user } = response.data;
        setForm(tokenForm);
        setSubmissionToken(tokenSubmission);
        setUserData(user);

        // Save the known profile so the submission is linked to the person.
        if (user && tokenSubmission) {
          try {
            await service.saveProfile(tokenSubmission, {
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              phone: user.phone || "",
              role: user.role || "volunteer",
              dob: user.dob || "",
              address: user.address || "",
              city: user.city || "",
              state: user.state || "",
              zip: user.zip || "",
              jobTitle: user.jobTitle || "",
            });
          } catch (profileErr) {
            // The submission is already linked through the user id.
            console.error("Error auto-saving profile:", profileErr);
          }
        }
      } else {
        setError({ message: "Assessment not found or is no longer active.", retryable: false });
      }
    } catch (err) {
      console.error("Error loading assessment by token:", err);
      setError({
        message: err.response?.data?.error || "Unable to load assessment. Please try again later.",
        retryable: isRetryableError(err),
      });
    } finally {
      setLoading(false);
    }
  }, [service, token]);

  useEffect(() => {
    fetchFormByToken();
  }, [fetchFormByToken]);

  const finishAssessment = useCallback(async () => {
    setPhase("submitting");
    setResults(calculateAssessmentResults(answers, questions));

    try {
      const frameworkId = form?.assessmentFramework?.id;
      if (submissionToken && frameworkId) {
        await service.saveAssessmentResponses(submissionToken, frameworkId, buildResponses(questions, answers));
      }
      setPhase("results");
    } catch (err) {
      console.error("Error submitting assessment:", err);
      setPhase("submit-error");
    }
  }, [service, answers, submissionToken, form, questions]);

  const handleAnswer = useCallback(
    (value) => {
      const question = questions[currentIndex];
      if (!question) return;

      setAnswers((prev) => applyAnswer(prev, question, value));

      // Auto-advance after a short pause so the selection registers visually.
      setTimeout(() => {
        if (currentIndex < totalQuestions - 1) {
          setCurrentIndex((prev) => prev + 1);
        } else {
          finishAssessment();
        }
      }, 400);
    },
    [currentIndex, questions, totalQuestions, finishAssessment]
  );

  const shellProps = { organizationName: organization.name, organizationLogo: organization.logo };

  if (loading) {
    return (
      <PublicSurveyShell center>
        <Loader size="lg" label="Loading assessment" />
      </PublicSurveyShell>
    );
  }

  if (error) {
    return (
      <PublicSurveyShell center>
        <ErrorState
          title="Assessment Unavailable"
          titleAs="h1"
          message={error.message}
          onRetry={error.retryable ? fetchFormByToken : undefined}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "intro") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <AssessmentIntro
          organizationName={organization.name}
          onBegin={() => {
            setPhase("questions");
            setCurrentIndex(0);
          }}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "questions" && questions[currentIndex]) {
    return (
      <PublicSurveyShell
        {...shellProps}
        progress={<SurveyProgress current={currentIndex + 1} total={totalQuestions} />}
      >
        <AssessmentQuestion
          key={questions[currentIndex].id}
          question={questions[currentIndex]}
          answers={answers}
          onAnswer={handleAnswer}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "submit-error") {
    return (
      <PublicSurveyShell {...shellProps} center>
        <ErrorState
          title="Responses Not Saved"
          titleAs="h1"
          message="We couldn't save your responses. Please try again."
          onRetry={finishAssessment}
        />
      </PublicSurveyShell>
    );
  }

  if (phase === "results" && results) {
    return (
      <PublicSurveyShell {...shellProps} panel={false}>
        <AssessmentResults
          results={results}
          organizationName={organization.name}
          userName={userData?.firstName}
        />
      </PublicSurveyShell>
    );
  }

  return (
    <PublicSurveyShell {...shellProps} center>
      <Loader size="lg" label="Calculating your results" />
      <p aria-hidden="true" className="text-center text-sm text-fg-muted">Calculating your results…</p>
    </PublicSurveyShell>
  );
};

export default IntakeTokenPage;
